import httpRequest from "@/apis/axios-api";

import {
  AiKeyTestResult,
  AiProvider,
  UserAiKey,
  UserInfo,
  UserInfoView,
  UserLoginRecordPage,
  UserModifyInfoRequest,
  UserModifyPasswordRequest,
  UserModifyEmailRequest,
  UserModifyVjudgeRequest,
  UserModifyUsernameRequest,
  UserModifyPhoneRequest,
  UserNotificationSettingRequest,
} from "@/types/user.ts";
import type { CodeResult } from "@/types/request.ts";
import { GetAvatarUrl } from "@/util/avatar.ts";
import { executeRecaptcha } from "@/util/recaptcha.ts";

export enum UserGender {
  Unknown = 0,
  Male = 1,
  Female = 2,
}

export function GetUserGenderKey(gender: UserGender): string {
  switch (gender) {
    case UserGender.Male:
      return "male";
    case UserGender.Female:
      return "female";
    default:
      return "unkown";
  }
}

export function ParseUser(item: UserInfo): UserInfoView {
  const result: Partial<UserInfoView> = {};
  result.id = item.id;
  result.username = item.username;
  result.nickname = item.nickname;
  result.email = item.email;
  result.slogan = item.slogan;
  result.organization = item.organization;
  result.gender = item.gender;

  if (item.blog != undefined) {
    // 判断item.source是否符合[]()格式，是否赋值result.sourceUrl
    const urlRegex = /\[([^\]]+)\]\(([^)]+)\)/;
    const match = item.blog.match(urlRegex);
    if (match) {
      result.blog = match[1];
      result.blogUrl = match[2];
    } else {
      result.blog = item.blog;
      result.blogUrl = "";
    }
  }

  result.accept = item.accept;
  result.attempt = item.attempt;
  result.vjudgeId = item.vjudge_id;
  result.avatar = GetAvatarUrl(item.username, item.email);

  // 添加等级和经验相关字段
  result.level = item.level || 0;
  result.experience = item.experience || 0;
  result.experience_current_level = item.experience_current_level || 0;
  result.experience_upgrade = item.experience_upgrade || 100;

  // 添加货币字段
  result.coin = item.coin || 0;

  // result 逐字段赋值，此处已填满 UserInfoView 的全部字段
  return result as UserInfoView;
}

export async function RequestLogin(username: string, password: string) {
  // 风险自适应人机验证：这里只是「尽力带上」令牌，后端仅在判定为可疑
  // （同账号连续失败 / 同来源 IP 大量失败）时才校验它。
  //
  // 拿不到令牌（脚本被广告拦截插件挡住、网络异常）不阻断登录：reCAPTCHA 是
  // 第三方脚本，把它变成登录链路的必经环节，等于让一个外部服务成为
  // 「所有人都登不进来」的单点故障。真正的风险判定在后端。
  let token = "";
  try {
    token = await executeRecaptcha("login");
  } catch (e) {
    console.warn("reCAPTCHA token unavailable, login continues without it", e);
  }

  return httpRequest({
    url: "/user/login",
    method: "post",
    data: {
      username: username,
      password: password,
      token: token,
    },
  });
}

export function PostLoginRefresh() {
  return httpRequest({
    url: "/user/login/refresh",
    method: "post",
  });
}

export function PostLogout() {
  return httpRequest({
    url: "/user/logout",
    method: "post",
  });
}

/** 用户主页接口返回的题目摘要（对应后端 foundationview.ProblemViewKey） */
export interface UserInfoProblemKey {
  id: number;
  key: string;
}

/** 用户主页接口返回的提交统计（对应后端 foundationview.JudgeJobCountStatics） */
export interface UserInfoJudgeStatics {
  accept: number;
  attempt: number;
  date: string;
}

export function GetUserInfo(username: string): Promise<
  CodeResult<{
    user: UserInfo;
    problems_ac?: UserInfoProblemKey[];
    problems_attempt?: UserInfoProblemKey[];
    statics?: UserInfoJudgeStatics[];
  }>
> {
  const params = {
    username: username,
  };
  return httpRequest({
    url: `/user/info?${new URLSearchParams(params).toString()}`,
    method: "get",
  });
}

export function GetUserModifyInfo() {
  return httpRequest({
    url: `/user/modify/info`,
    method: "get",
  });
}

export function PostUserModifyInfo(request: UserModifyInfoRequest) {
  return httpRequest({
    url: "/user/modify",
    method: "post",
    data: request,
  });
}

export function PostUserModifyPassword(request: UserModifyPasswordRequest) {
  return httpRequest({
    url: "/user/modify/password",
    method: "post",
    data: request,
  });
}

export function PostUserModifyEmail(request: UserModifyEmailRequest) {
  return httpRequest({
    url: "/user/modify/email",
    method: "post",
    data: request,
  });
}

export function PostUserModifyPhoneKey(token: string, phone: string) {
  return httpRequest({
    url: "/user/modify/phone/key",
    method: "post",
    data: {
      token: token,
      phone: phone,
    },
  });
}

export function PostUserModifyPhone(request: UserModifyPhoneRequest) {
  return httpRequest({
    url: "/user/modify/phone",
    method: "post",
    data: request,
  });
}

export function PostUserModifyVjudge(request: UserModifyVjudgeRequest) {
  return httpRequest({
    url: "/user/modify/vjudge",
    method: "post",
    data: request,
  });
}

export function PostUserModifyUsername(request: UserModifyUsernameRequest) {
  return httpRequest({
    url: "/user/modify/username",
    method: "post",
    data: request,
  });
}

export function PostUserModifyOldEmailKey(token: string) {
  return httpRequest({
    url: "/user/modify/email/key/old",
    method: "post",
    data: {
      token: token,
    },
  });
}

export function PostUserModifyEmailKey(token: string, email: string) {
  return httpRequest({
    url: "/user/modify/email/key",
    method: "post",
    data: {
      token: token,
      email: email,
    },
  });
}

export function GetVjudgeAcProblem(username: string) {
  const params = {
    username: username,
  };
  return httpRequest({
    url: `/vjudge/user/solve/detail?${new URLSearchParams(params).toString()}`,
    method: "get",
  });
}

export function PostUserAccountInfoss(userIds: number[]) {
  return httpRequest({
    url: "/user/account/infos",
    method: "post",
    data: {
      users: userIds,
    },
  });
}

export function PostUserParse(usernames: string[]) {
  return httpRequest({
    url: "/user/parse",
    method: "post",
    data: {
      users: usernames,
    },
  });
}

export function PostUserRegisterEmailKey(token: string, email: string) {
  return httpRequest({
    url: "/user/register/email",
    method: "post",
    data: {
      token: token,
      email: email,
    },
  });
}

export function PostUserRegister(
  username: string,
  password: string,
  email: string,
  key: string,
  nickname: string,
  gender: string,
  realName: string,
  organization: string
) {
  return httpRequest({
    url: "/user/register",
    method: "post",
    data: {
      username: username,
      password: password,
      email: email,
      key: key,
      nickname: nickname,
      gender: gender,
      real_name: realName,
      organization: organization,
    },
  });
}

export function PostUserLoginForget(token: string, username: string) {
  return httpRequest({
    url: "/user/forget",
    method: "post",
    data: {
      token: token,
      username: username,
    },
  });
}

export function PostUserPasswordForget(username: string, password: string, key: string) {
  return httpRequest({
    url: "/user/password/forget",
    method: "post",
    data: {
      username: username,
      password: password,
      key: key,
    },
  });
}

// 签到接口
export function PostCheckin() {
  return httpRequest({
    url: "/user/checkin",
    method: "post",
  });
}

// 获取今日签到人数
export function GetCheckinToday() {
  return httpRequest({
    url: "/user/checkin/today",
    method: "get",
  });
}

// 获取当前用户的通知偏好设置
export function GetUserNotificationSetting() {
  return httpRequest({
    url: "/user/notification/setting",
    method: "get",
  });
}

// 更新当前用户的通知偏好设置
export function PostUserNotificationSetting(request: UserNotificationSettingRequest) {
  return httpRequest({
    url: "/user/notification/setting",
    method: "post",
    data: request,
  });
}

// 获取当前用户的登录记录（游标分页，按时间倒序）
export function GetLoginRecords(cursorId = 0, limit = 20): Promise<CodeResult<UserLoginRecordPage>> {
  return httpRequest({
    url: "/user/login/records",
    method: "get",
    params: {
      cursor_id: cursorId,
      limit: limit,
    },
  });
}

// ===== 用户自带 AI 密钥（BYOK）=====

// 获取受支持的 AI 厂商白名单（供下拉框）
export function GetAiKeyProvider(): Promise<CodeResult<AiProvider[]>> {
  return httpRequest({
    url: "/user/ai-key/provider",
    method: "get",
  });
}

// 列出当前用户已保存的密钥（仅掩码）
export function GetAiKeyList(): Promise<CodeResult<UserAiKey[]>> {
  return httpRequest({
    url: "/user/ai-key/list",
    method: "get",
  });
}

// 保存一条密钥
export function PostAiKeyAdd(provider: string, apiKey: string, model: string, remark: string): Promise<CodeResult<{ id: number }>> {
  return httpRequest({
    url: "/user/ai-key/add",
    method: "post",
    data: {
      provider: provider,
      api_key: apiKey,
      model: model,
      remark: remark,
    },
  });
}

// 删除一条密钥
export function PostAiKeyDelete(id: number) {
  return httpRequest({
    url: "/user/ai-key/delete",
    method: "post",
    data: { id: id },
  });
}

// 把某条密钥设为生效
export function PostAiKeyActive(id: number) {
  return httpRequest({
    url: "/user/ai-key/active",
    method: "post",
    data: { id: id },
  });
}

/**
 * 测试连接。
 * 传 id 表示测试已保存的密钥；传 provider + apiKey 表示测试尚未保存的密钥。
 * 后端超时 15 秒，这里把 axios 超时放宽到 30 秒以免前端先断
 */
export function PostAiKeyTest(params: { id?: number; provider?: string; apiKey?: string; model?: string }): Promise<CodeResult<AiKeyTestResult>> {
  return httpRequest({
    url: "/user/ai-key/test",
    method: "post",
    timeout: 30000,
    data: {
      id: params.id ?? 0,
      provider: params.provider ?? "",
      api_key: params.apiKey ?? "",
      model: params.model ?? "",
    },
  });
}
